import { useEffect, useState } from "react";
import { ActionIcon, Badge, Container, Group, Loader, Select, Stack, Text } from "@mantine/core";
import { IconCheck, IconCash, IconCashOff, IconPencil, IconPlus, IconPrinter, IconTrash, IconX } from "@tabler/icons-react";
import { DataTable } from "mantine-datatable";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import HttpClient from "../helpers/HttpClient";
import { Invoice, InvoiceStatus } from "../types/invoice.type";
import { InvoiceSetting } from "../types/invoice-setting.type";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { generateInvoicePdf } from "../helpers/invoicePdf";
import { useLanguage } from "../providers/LanguageContext";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => {
  const y = String(currentYear - i);
  return { value: y, label: y };
});

function InvoiceList() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [settings, setSettings] = useState<InvoiceSetting | undefined>(undefined);
  const [year, setYear] = useState<string>(String(currentYear));
  const [status, setStatus] = useState<InvoiceStatus>("all");
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [confirmAction, setConfirmAction] = useState<"delete" | "cancel" | null>(null);
  const navigate = useNavigate();

  const STATUS_OPTIONS = [
    { value: "all", label: t.invoice.filterAll },
    { value: "open", label: t.invoice.statusOpen },
    { value: "fixed", label: t.invoice.statusFixed },
    { value: "paid", label: t.invoice.statusPaid },
    { value: "unpaid", label: t.invoice.statusUnpaid },
    { value: "cancelled", label: t.invoice.statusCancelled },
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (year) params.year = year;
      if (status !== "all") params.status = status;
      const [result, s] = await Promise.all([
        new HttpClient<Invoice>("/invoice").getAll(params),
        new HttpClient<InvoiceSetting>("/invoice-setting").getSingle().catch(() => undefined),
      ]);
      setInvoices(result);
      if (s) setSettings(s);
    } catch {
      notifications.show({ message: t.invoice.loadFailed, color: "red" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [year, status]);

  const handleDelete = async () => {
    if (confirmId === null) return;
    try {
      const res = await new HttpClient<Invoice>("/invoice").delete(confirmId);
      if (res.status !== 200) throw new Error();
      notifications.show({ message: t.invoice.deleteSuccess, color: "green" });
      fetchData();
    } catch {
      notifications.show({ message: t.invoice.deleteFailed, color: "red" });
    } finally {
      setConfirmId(null);
      setConfirmAction(null);
    }
  };

  const handleCancel = async () => {
    if (confirmId === null) return;
    try {
      await new HttpClient<Invoice>("/invoice").postAction(`${confirmId}/cancel`, {});
      notifications.show({ message: t.invoice.cancelSuccess, color: "orange" });
      fetchData();
    } catch {
      notifications.show({ message: t.invoice.cancelFailed, color: "red" });
    } finally {
      setConfirmId(null);
      setConfirmAction(null);
    }
  };

  const handleFix = async (inv: Invoice) => {
    try {
      await new HttpClient<Invoice>("/invoice").postAction(`${inv.id}/fix`, {});
      notifications.show({ message: t.invoice.fixSuccess, color: "green" });
      fetchData();
    } catch {
      notifications.show({ message: t.invoice.fixFailed, color: "red" });
    }
  };

  const handleMarkPaid = async (inv: Invoice) => {
    try {
      await new HttpClient<Invoice>("/invoice").postAction(`${inv.id}/mark-paid`, { paidDate: new Date().toISOString() });
      notifications.show({ message: t.invoice.paidSuccess, color: "green" });
      fetchData();
    } catch {
      notifications.show({ message: t.invoice.paidFailed, color: "red" });
    }
  };

  const handleMarkUnpaid = async (inv: Invoice) => {
    try {
      await new HttpClient<Invoice>("/invoice").postAction(`${inv.id}/mark-unpaid`, {});
      notifications.show({ message: t.invoice.paidSuccess, color: "blue" });
      fetchData();
    } catch {
      notifications.show({ message: t.invoice.paidFailed, color: "red" });
    }
  };

  const handlePrint = async (inv: Invoice) => {
    await generateInvoicePdf(inv, settings);
  };

  const totalFor = (inv: Invoice) => {
    const sub = (inv.positions ?? []).reduce((s, p) => s + Number(p.quantity) * Number(p.unitPrice), 0);
    const vat = sub * (Number(inv.vatRate) / 100);
    return sub + vat;
  };

  const statusBadge = (inv: Invoice) => {
    if (inv.isCancellationDocument) return <Badge color="red">{t.invoice.cancellationBadge}</Badge>;
    if (inv.isCancelled) return <Badge color="red">{t.invoice.cancelledBadge}</Badge>;
    if (inv.isFixed && inv.isPaid) {
      return (
        <Stack gap={2}>
          <Badge color="teal">{t.invoice.paidBadge}</Badge>
          {inv.paidDate && <Text size="xs" c="dimmed">{new Date(inv.paidDate).toLocaleDateString("de-DE")}</Text>}
        </Stack>
      );
    }
    if (inv.isFixed) return <Badge color="green">{t.invoice.statusFixed}</Badge>;
    return <Badge color="blue">{t.invoice.statusOpen}</Badge>;
  };

  return (
    <Container fluid pb={10} mt={10}>
      <ConfirmDialog
        opened={confirmId !== null}
        onClose={() => { setConfirmId(null); setConfirmAction(null); }}
        onConfirm={confirmAction === "delete" ? handleDelete : handleCancel}
        title={confirmAction === "delete" ? t.invoice.confirmDeleteTitle : t.invoice.confirmCancelTitle}
        message={confirmAction === "delete" ? t.invoice.confirmDeleteMsg : t.invoice.confirmCancelMsg}
        confirmLabel={confirmAction === "delete" ? t.common.delete : t.invoice.cancelAction}
        cancelLabel={t.common.cancel}
      />
      <Group gap={25} visibleFrom="xs" mb="md">
        <h1>{t.invoice.title}</h1>
        <ActionIcon
          variant="filled"
          size="xl"
          radius="xl"
          aria-label={t.invoice.newInvoice}
          onClick={() => navigate("/invoice/create")}
        >
          <IconPlus style={{ width: "70%", height: "70%" }} stroke={1.5} />
        </ActionIcon>
      </Group>
      <Group mb="md" gap="sm">
        <Select
          label={t.invoice.filterYear}
          data={YEAR_OPTIONS}
          value={year}
          onChange={(v) => setYear(v ?? String(currentYear))}
          w={100}
          allowDeselect={false}
        />
        <Select
          label={t.invoice.filterStatus}
          data={STATUS_OPTIONS}
          value={status}
          onChange={(v) => setStatus((v ?? "all") as InvoiceStatus)}
          w={180}
          allowDeselect={false}
        />
      </Group>
      {loading ? (
        <Loader size="md" />
      ) : (
        <DataTable
          withTableBorder
          borderRadius="sm"
          withColumnBorders
          striped
          highlightOnHover
          records={invoices}
          idAccessor="id"
          columns={[
            {
              accessor: "invoiceNumber",
              title: t.invoice.colInvoiceNumber,
              width: "15%",
              render: (inv) => (
                <Group gap={4} wrap="nowrap">
                  <Text size="sm">{inv.invoiceNumber ?? <Text span c="dimmed">–</Text>}</Text>
                  {inv.relatedInvoiceId && (
                    <Badge size="xs" color={inv.isCancellationDocument ? "red" : "orange"} variant="outline">
                      {inv.isCancellationDocument ? t.invoice.relatedCancellation : `→ ${inv.relatedInvoiceNumber ?? `#${inv.relatedInvoiceId}`}`}
                    </Badge>
                  )}
                </Group>
              ),
            },
            {
              accessor: "invoiceDate",
              title: t.invoice.colDate,
              width: "12%",
              render: (inv) => new Date(inv.invoiceDate).toLocaleDateString("de-DE"),
            },
            {
              accessor: "recipientName",
              title: t.invoice.colRecipient,
              render: (inv) => (
                <Text size="sm">
                  {[inv.recipientLine1, inv.recipientName].filter(Boolean).join(" – ")}
                </Text>
              ),
            },
            {
              accessor: "total",
              title: t.invoice.colTotal,
              width: "13%",
              render: (inv) =>
                `€ ${totalFor(inv).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            },
            {
              accessor: "status",
              title: t.invoice.colStatus,
              width: "14%",
              render: (inv) => statusBadge(inv),
            },
            {
              accessor: "actions",
              title: "",
              width: "15%",
              render: (inv) => (
                <Group gap={4} justify="center" wrap="nowrap">
                  {!inv.isFixed && !inv.isCancelled && (
                    <ActionIcon
                      variant="light"
                      color="blue"
                      onClick={() => navigate(`/invoice/edit/${inv.id}`)}
                      aria-label={t.common.edit}
                    >
                      <IconPencil size={18} />
                    </ActionIcon>
                  )}
                  <ActionIcon
                    variant="light"
                    color="gray"
                    onClick={() => handlePrint(inv)}
                    aria-label={t.invoice.pdfPrint}
                  >
                    <IconPrinter size={18} />
                  </ActionIcon>
                  {!inv.isFixed && !inv.isCancelled && (
                    <ActionIcon
                      variant="light"
                      color="green"
                      onClick={() => handleFix(inv)}
                      aria-label={t.invoice.statusFixed}
                      title={t.invoice.statusFixed}
                    >
                      <IconCheck size={18} />
                    </ActionIcon>
                  )}
                  {inv.isFixed && !inv.isCancelled && !inv.isPaid && (
                    <ActionIcon
                      variant="light"
                      color="teal"
                      onClick={() => handleMarkPaid(inv)}
                      aria-label={t.invoice.markPaid}
                      title={t.invoice.markPaid}
                    >
                      <IconCash size={18} />
                    </ActionIcon>
                  )}
                  {inv.isFixed && !inv.isCancelled && inv.isPaid && (
                    <ActionIcon
                      variant="light"
                      color="gray"
                      onClick={() => handleMarkUnpaid(inv)}
                      aria-label={t.invoice.markUnpaid}
                      title={t.invoice.markUnpaid}
                    >
                      <IconCashOff size={18} />
                    </ActionIcon>
                  )}
                  {inv.isFixed && !inv.isCancelled && (
                    <ActionIcon
                      variant="light"
                      color="orange"
                      onClick={() => { setConfirmId(Number(inv.id)); setConfirmAction("cancel"); }}
                      aria-label={t.invoice.cancellationBadge}
                    >
                      <IconX size={18} />
                    </ActionIcon>
                  )}
                  {!inv.isFixed && !inv.isCancelled && (
                    <ActionIcon
                      variant="light"
                      color="red"
                      onClick={() => { setConfirmId(Number(inv.id)); setConfirmAction("delete"); }}
                      aria-label={t.common.delete}
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                  )}
                </Group>
              ),
            },
          ]}
        />
      )}
    </Container>
  );
}

export default InvoiceList;
